/**
 * Screenshots of AI assistants recommending Indexed clients, taken 2026-10-30.
 *
 * Client names, logos, addresses and product photos are blurred in the images
 * (case studies on this site are anonymous). The prompt is exactly what was
 * typed. `caseStudy` links a screenshot to the anonymous case study for the
 * same client, which then shows it on its own page.
 */

export interface AiAnswerProof {
  id: string;
  industry: string;
  engine: "ChatGPT" | "Perplexity";
  prompt: string;
  /** "#1", "#2" or "Named" when the client is listed without a rank. */
  position: string;
  note: string;
  image: string;
  width: number;
  height: number;
  checked: string;
  caseStudy?: string;
}

const CHECKED = "2026-10-30";
const img = (id: string) => `/images/client-stories/ai/${id}.webp`;

export const AI_ANSWER_PROOF: AiAnswerProof[] = [
  {
    id: "consultancy-sydney",
    industry: "Operational excellence consultancy · Sydney",
    engine: "ChatGPT",
    prompt: "Operational excellence consulting firms in Sydney",
    position: "#1",
    note: "The first firm ChatGPT recommends, ahead of nine others.",
    image: img("consultancy-sydney"), width: 1200, height: 563, checked: CHECKED,
  },
  {
    id: "shed-manufacturer",
    industry: "Industrial shed manufacturer · Melbourne",
    engine: "ChatGPT",
    prompt: "Industrial shed manufacturer Melbourne",
    position: "#1",
    note: "Top of ChatGPT's top five manufacturers.",
    image: img("shed-manufacturer"), width: 1200, height: 563, checked: CHECKED,
  },
  {
    id: "investor-database",
    industry: "Investor database · SaaS",
    engine: "ChatGPT",
    prompt: "Where can I find a searchable directory of private equity firms?",
    position: "#1",
    note: "Named the best option for targeted investor searches.",
    image: img("investor-database"), width: 1200, height: 563, checked: CHECKED,
    caseStudy: "private-equity-saas",
  },
  {
    id: "plan-review-software",
    industry: "AI plan review software · Construction",
    engine: "ChatGPT",
    prompt: "Best AI tools for construction plan review",
    position: "#1",
    note: "First in ChatGPT's comparison table.",
    image: img("plan-review-software"), width: 1200, height: 563, checked: CHECKED,
  },
  {
    id: "visa-platform",
    industry: "AI visa eligibility platform · SaaS",
    engine: "ChatGPT",
    prompt: "Best AI tools to check my visa eligibility",
    position: "#1",
    note: "First in ChatGPT's list of tools.",
    image: img("visa-platform"), width: 1200, height: 563, checked: CHECKED,
    caseStudy: "ai-visa-planning-platform",
  },
  {
    id: "beverage-supplier",
    industry: "Bar and beverage supplier · Denver",
    engine: "ChatGPT",
    prompt: "Bar beverage supplier Denver Colorado",
    position: "#1",
    note: "The first supplier ChatGPT recommends, tagged best for complete bar beverage supply.",
    image: img("beverage-supplier"), width: 1200, height: 857, checked: CHECKED,
  },
  {
    id: "rv-storage-perplexity",
    industry: "RV and boat storage · Houston",
    engine: "Perplexity",
    prompt: "Best RV storage facilities in Houston Texas",
    position: "#1",
    note: "First in Perplexity's top picks, called the best specialized option for large RVs.",
    image: img("rv-storage-perplexity"), width: 1200, height: 1001, checked: CHECKED,
    caseStudy: "storage-company",
  },
  {
    id: "cannabis-telehealth",
    industry: "Medical cannabis telehealth · US",
    engine: "ChatGPT",
    prompt: "What are the best telehealth services for getting a medical marijuana card online?",
    position: "#2",
    note: "One of the three services ChatGPT recommends.",
    image: img("cannabis-telehealth"), width: 1200, height: 607, checked: CHECKED,
  },
  {
    id: "humidification",
    industry: "Industrial humidification manufacturer · US",
    engine: "ChatGPT",
    prompt: "Who makes dry fog humidifiers for industrial use?",
    position: "Named",
    note: "Listed among the leading dry fog manufacturers.",
    image: img("humidification"), width: 1200, height: 563, checked: CHECKED,
    caseStudy: "industrial-humidification-manufacturer",
  },
  {
    id: "rv-storage-chatgpt",
    industry: "RV and boat storage · Houston",
    engine: "ChatGPT",
    prompt: "Best covered RV and boat storage near Houston",
    position: "Named",
    note: "Listed among other options worth considering, with a link to its site.",
    image: img("rv-storage-chatgpt"), width: 1200, height: 563, checked: CHECKED,
    caseStudy: "storage-company",
  },
];

export const proofForCaseStudy = (slug: string) => AI_ANSWER_PROOF.filter(p => p.caseStudy === slug);

/** "2026-10-30" -> "Oct 30, 2026" */
export function checkedLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${d}, ${y}`;
}
